/**
 * Camera storage passphrases – per-account, per-device, per-era password key
 * hex (PBKDF2 of the user's passphrase).
 *
 * @remarks
 * Persists only the password-key hex, never the raw password. Scoped by
 * account → device → era as `{ [email]: { [deviceId]: { [createdAt]: passwordKeyHex } } }`.
 * Each rotated backup has its own salt (so the same password derives a different
 * key per era); one entry per era lets a single prompt unlock every rotated era.
 */

import { get, writable } from 'svelte/store';
import { user_data } from 'src/stores';
import type { CameraStoragePassphraseMap } from '../helpers/encryption/types/master-key';

/** localStorage key under which per-account, per-device, per-era password keys are stored. */
const STORAGE_KEY = 'camera_storage_passphrases';

const readInitial = (): CameraStoragePassphraseMap => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? (parsed as CameraStoragePassphraseMap) : {};
	} catch {
		return {};
	}
};

function create_camera_storage_passphrases() {
	const { subscribe, set, update } = writable<CameraStoragePassphraseMap>(readInitial());

	return {
		subscribe,
		set,
		/** Persists the password-key hex for a device's era (`createdAt`) under the active account. */
		setPasswordKey: (deviceId: string, passwordKeyHex: string, createdAt: string) => {
			if (!deviceId || !passwordKeyHex) return;
			const email = user_data.getEmail();
			if (!email) return;
			update((state) => {
				const account = state[email] ?? {};
				const device = { ...(account[deviceId] ?? {}), [createdAt ?? '']: passwordKeyHex };
				return { ...state, [email]: { ...account, [deviceId]: device } };
			});
		},
		/** Reads the password-key hex for a device's era, or `null`. */
		getPasswordKey: (deviceId: string, createdAt: string): string | null => {
			if (!deviceId) return null;
			const email = user_data.getEmail();
			if (!email) return null;
			return get(camera_storage_passphrases)[email]?.[deviceId]?.[createdAt ?? ''] ?? null;
		},
		/** Removes an entire account's password keys (used on a real logout of that account). */
		removeAccount: (email: string) =>
			update((state) => {
				const key = email.trim().toLowerCase();
				if (!key || !state[key]) return state;
				const next = { ...state };
				delete next[key];
				return next;
			}),
		/** Removes a device's era (or the whole device when `createdAt` is omitted). */
		remove: (deviceId: string, createdAt?: string) =>
			update((state) => {
				const email = user_data.getEmail();
				if (!email || !state[email]?.[deviceId]) return state;
				const account = { ...state[email] };
				if (createdAt == null) {
					delete account[deviceId];
				} else {
					const device = { ...account[deviceId] };
					delete device[createdAt];
					account[deviceId] = device;
				}
				return { ...state, [email]: account };
			}),
		clear: () => set({}),
	};
}

export const camera_storage_passphrases = create_camera_storage_passphrases();
camera_storage_passphrases.subscribe((val) => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(val));
	} catch {
		/** storage may be unavailable (private mode / quota) */
	}
});
